import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Check, RotateCcw, Loader2, User, Bot, ClipboardList } from "lucide-react";
import { toast } from "sonner";

function newSession() {
  const s = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem("ai_session_id", s);
  return s;
}
function getSession() {
  return localStorage.getItem("ai_session_id") || newSession();
}

// Minimal **bold** + newline renderer
function Rich({ text }) {
  if (!text) return null;
  return (
    <div className="whitespace-pre-wrap leading-relaxed">
      {text.split("\n").map((line, i) => (
        <div key={i}>
          {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith("**") && part.endsWith("**")
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : <span key={j}>{part}</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AiChatPanel({ variant = "page" }) {
  const [sessionId, setSessionId] = useState(getSession);
  const [messages, setMessages] = useState([]); // {role, content, plan?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quick, setQuick] = useState([]);
  const scrollRef = useRef(null);

  const load = async (sid) => {
    try {
      const { data } = await api.get(`/ai/conversation/${sid}`);
      const msgs = (data.messages || []).map((m) => ({ role: m.role, content: m.content }));
      setMessages(msgs);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load(sessionId);
    api.get("/ai/quick-actions").then(({ data }) => setQuick(data.actions || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, plan: data.plan }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yapay zeka hatası");
      setMessages((m) => [...m, { role: "assistant", content: "Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin." }]);
    } finally {
      setLoading(false);
    }
  };

  const approve = async (idx, planId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/apply", { plan_id: planId });
      setMessages((m) => m.map((msg, i) => i === idx ? { ...msg, plan: { ...msg.plan, applied: true } } : msg));
      setMessages((m) => [...m, { role: "system", content: `✅ ${data.summary}`, results: data.results }]);
      toast.success("Plan takvime uygulandı");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Uygulanamadı");
    } finally {
      setLoading(false);
    }
  };

  const newChat = async () => {
    try { await api.delete(`/ai/conversation/${sessionId}`); } catch (e) {}
    const s = newSession();
    setSessionId(s);
    setMessages([]);
    toast.success("Yeni sohbet başlatıldı");
  };

  const isBubble = variant === "bubble";

  return (
    <div className={`flex flex-col ${isBubble ? "h-full" : "h-[calc(100vh-13rem)] min-h-[500px] rounded-lg border border-zinc-200 bg-white"}`} data-testid="ai-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-zinc-200 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white">
            <Sparkles size={16} />
          </div>
          <div>
            <div className="font-medium text-sm leading-tight">Planlama Asistanı</div>
            <div className="text-xs text-zinc-400 leading-tight">Gemini destekli üretim planlama</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={newChat} data-testid="ai-new-chat" className="text-zinc-500">
          <RotateCcw size={14} className="mr-1" />Yeni
        </Button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="text-center text-zinc-400 text-sm mt-8 px-4">
            <Bot size={32} className="mx-auto mb-3 text-zinc-300" />
            Aktif projelerdeki ürünleri ustalara dağıtmam, takvime iş eklemem, düzenlemem veya silmem için
            bana yazın. Aşağıdaki hazır komutlardan da başlayabilirsiniz.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`ai-message-${i}`}
               className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            {m.role !== "system" && (
              <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-zinc-800 text-white" : "bg-violet-100 text-violet-700"}`}>
                {m.role === "user" ? <User size={14} /> : <Bot size={14} />}
              </div>
            )}
            <div className={`max-w-[85%] text-sm ${m.role === "system" ? "mx-auto" : ""}`}>
              {m.role === "system" ? (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 text-center">{m.content}</div>
              ) : (
                <div className={`rounded-lg px-3 py-2 ${m.role === "user" ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-800"}`}>
                  <Rich text={m.content} />
                </div>
              )}
              {/* Plan card */}
              {m.plan && (
                <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="ai-plan-card">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-violet-800 mb-2">
                    <ClipboardList size={13} /> Önerilen Plan · {m.plan.actions.length} işlem
                  </div>
                  <ul className="space-y-1 mb-2.5">
                    {m.plan.actions.map((a, j) => (
                      <li key={j} className={`text-xs flex gap-1.5 ${a.error ? "text-red-600" : "text-zinc-700"}`}>
                        <span className="text-violet-400">•</span>
                        <span>{a.human || a.error}</span>
                      </li>
                    ))}
                  </ul>
                  {m.plan.applied ? (
                    <div className="text-xs text-emerald-700 flex items-center gap-1"><Check size={13} /> Uygulandı</div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => approve(i, m.plan.id)} disabled={loading}
                              data-testid="ai-plan-approve" className="bg-violet-600 hover:bg-violet-700 h-8">
                        <Check size={14} className="mr-1" />Onayla ve Uygula
                      </Button>
                      <span className="text-xs text-zinc-400">İtiraz için aşağıdan yazın</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center shrink-0"><Bot size={14} /></div>
            <div className="rounded-lg px-3 py-2 bg-zinc-100 text-zinc-500 text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />Düşünüyor...
            </div>
          </div>
        )}
      </div>

      {/* Quick actions */}
      {quick.length > 0 && (
        <div className="px-4 pt-2 flex flex-wrap gap-1.5 shrink-0">
          {quick.map((q, i) => (
            <button key={i} onClick={() => send(q.prompt)} disabled={loading}
                    data-testid={`ai-quick-action-${i}`}
                    className="text-xs px-2.5 py-1 rounded-full border border-zinc-200 bg-white hover:bg-violet-50 hover:border-violet-300 text-zinc-600 disabled:opacity-50 transition-colors">
              {q.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 flex items-center gap-2 border-t border-zinc-200 shrink-0 mt-2">
        <Input
          data-testid="ai-chat-input"
          placeholder="Bir komut yazın... (ör. 'Kapı üretimini Ali Usta'ya yarın ata')"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={loading}
        />
        <Button onClick={() => send()} disabled={loading || !input.trim()}
                data-testid="ai-send-button" className="bg-violet-600 hover:bg-violet-700 shrink-0">
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
}
